export type HttpMethod = 'get' | 'post' | 'delete' | 'put';

export type ParsedJSON = any;

export class ApiSpecialResponse
{
    public static readonly Empty = new ApiSpecialResponse('empty');
    public static readonly NoContent = new ApiSpecialResponse('no_content');

    private constructor(private _value:string)
    {
    }
}

export class ApiError
{
    constructor(public readonly code:number, public message:string, public error:any | null)
    {
    }

    public static isError(response:any):boolean
    {
        return response instanceof ApiError;
    }

    public static from(obj:any):ApiError
    {
        if( obj )
        {
            const code = obj.code ? obj.code : -1;
            const msg = obj.message ? obj.message : '';

            return new ApiError(code, msg, null);
        }
        else
            return new ApiError(106, 'Unknown error', null);
    }
}

export type OnUploadProgress = (e:ProgressEvent) => any;

export type FetcherResponse = ParsedJSON | ApiError | ApiSpecialResponse;

export class Fetcher
{
    private _authToken:any = null;

    constructor(private _baseUrl?:string, _authToken:string = null)
    {
        this._authToken = _authToken;
    }

    public overrideBaseUrl(newBaseUrl:string) {
        return new Fetcher(newBaseUrl, this._authToken);
    }

    public setAuthToken(authToken:string)
    {
        this._authToken = authToken;
    }

    public async request(httpMethod:HttpMethod,
                         apiPath:string,
                         responseTypeCtor:any,
                         queryParams:any = null,
                         postBody:any = null,
                         headers:Headers = null,
                         onUploadProgress?:OnUploadProgress,
                         uploadCancelledPromise?:Promise<any>):Promise<FetcherResponse>
    {
        if( queryParams !== null )
        {
            apiPath = `${apiPath}?${this._encodeQueryParams(queryParams)}`;
        }

        const requestUrl = this._toFullURL(apiPath);
        const requestHeaders = this._buildRequestHeaders(httpMethod, postBody, headers);
        const requestBody = this._buildRequestBody(postBody, requestHeaders.get('Content-Type'));

        try
        {
            let response:FetcherResponse = null;

            if( onUploadProgress )
            {
                // falling back to XHR since fetch itself currently doesn't support progress callback for upload
                response = await this._XHR(
                    requestUrl,
                    httpMethod,
                    requestBody,
                    requestHeaders,
                    onUploadProgress,
                    uploadCancelledPromise
                );
            }
            else
            {
                response = await this._fetch(
                    requestUrl,
                    httpMethod,
                    requestBody,
                    requestHeaders
                );
            }

            if(
                response instanceof ApiError ||
                response instanceof ApiSpecialResponse ||
                !responseTypeCtor
            )
                return response;
            else
                return new responseTypeCtor(response);
        }
        catch( err )
        {
            if( err.canceled )
                throw err;

            return new ApiError(102, `Error: error caught in processing HTTP request for URL: ${requestUrl} error: ${err}`, err);
        }
    }

    public async requestWithoutProcessing(httpMethod:HttpMethod,
                                          apiPath:string,
                                          queryParams:any = null,
                                          postBody:any = null,
                                          headers:Headers = null):Promise<Response>
    {
        if( queryParams !== null )
        {
            apiPath = `${apiPath}?${this._encodeQueryParams(queryParams)}`;
        }

        const requestUrl = this._toFullURL(apiPath);
        const requestHeaders = this._buildRequestHeaders(httpMethod, postBody, headers);
        const requestBody = this._buildRequestBody(postBody, requestHeaders.get('Content-Type'));

        return await fetch(
            requestUrl, {
                redirect: 'manual',
                method: httpMethod,
                headers: requestHeaders,
                body: requestBody
            });
    }

    private async _fetch(url:string,
                         method:HttpMethod,
                         body:any,
                         headers:Headers):Promise<FetcherResponse>
    {
        const response:Response = await fetch(url, {redirect: 'manual', method, headers, body});

        if( !response )
            return new ApiError(100, `No response from fetch promise resolution`, null);

        try
        {
            const text:string = await response.text();

            return this._processResponse(text, response.status, url);
        }
        catch( err )
        {
            return new ApiError(105, `Error: could not get .text() from response for URL: ${url}`, err);
        }
    }

    private async _XHR(url:string,
                       method:HttpMethod,
                       body:any,
                       headers:Headers,
                       onUploadProgress:OnUploadProgress,
                       uploadCancelledPromise?:Promise<any>):Promise<FetcherResponse>
    {
        return new Promise((resolve, reject) =>
        {
            const request = new XMLHttpRequest();

            request.open(method, url);
            headers.forEach((value, name) => request.setRequestHeader(name, value));

            request.upload.onprogress = onUploadProgress;

            request.onload = () => resolve(this._processResponse(request.response, request.status, url));

            request.onerror = (ev:ErrorEvent) => reject(new Error(ev.error));

            if( uploadCancelledPromise )
            {
                uploadCancelledPromise.then(() =>
                {
                    reject({canceled: true, message: 'canceled'});
                    request.abort();
                });
            }

            request.send(body);
        });
    }

    private _buildRequestHeaders(httpMethod:HttpMethod, postBody:any, headers:Headers):Headers
    {
        const requestHeaders:Headers = new Headers({'Accept': 'application/json'});

        // fetch will generate correct content-type and boundary headers for FormData body
        if( httpMethod !== 'get' && !(postBody instanceof FormData) )
        {
            requestHeaders.set('Content-Type', 'application/json');
        }

        if( this._authToken !== null )
        {
            requestHeaders.set('Authorization', 'Workspace ' + this._authToken);
        }

        if( headers )
        {
            headers.forEach((value, name) => requestHeaders.set(name, value));
        }

        return requestHeaders;
    }

    private _buildRequestBody(postBody:any, contentType:string):any
    {
        return postBody && contentType === 'application/json' ? JSON.stringify(postBody) : postBody;
    }

    private async _processFetchResponse(fullURL:string, response:any):Promise<FetcherResponse>
    {

    }

    private async _processXHRResponse(fullURL:string, response:any):Promise<FetcherResponse>
    {

    }

    private _processResponse(data:string, status:number, fullURL:string):FetcherResponse
    {
        if( status === 204 )
            return ApiSpecialResponse.NoContent;

        if( !data || data.length === 0 )
        {
            return ApiSpecialResponse.Empty;
        }
        else
        {
            let jsonObj = null;

            try
            {
                jsonObj = JSON.parse(data);
            }
            catch( err )
            {
                return new ApiError(104, `Error: could not parse JSON from response at URL: ${fullURL}`, err);
            }

            if( status >= 400 && status <= 499 )
                return ApiError.from(jsonObj);
            else if( status >= 500 )
                return new ApiError(103, '500x: internal server error', null);
            else
                return jsonObj;
        }
    }

    private _encodeQueryParams(queryParmsObj:any)
    {
        return Object.keys(queryParmsObj)
            .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(queryParmsObj[k]))
            .join('&');
    }

    private _toFullURL(apiPath:string):string
    {
        apiPath = apiPath.trim();

        if( !this._baseUrl )
            return apiPath;

        if( apiPath.charAt(0) === '/' )
            apiPath = apiPath.substring(1, apiPath.length);

        return `${this._baseUrl}/${apiPath}`;
    }
}
